/**
 * One query-state boundary for the whole console (UX-03).
 *
 * High-traffic routes checked `isLoading` and then returned `null` when the data
 * was absent — Nav, EntityDetail, and every list. So an RLS denial, a schema the
 * gateway does not expose, an uninstalled module and a dropped network
 * connection ALL rendered as the same thing: an empty product with no
 * explanation and nothing to press.
 *
 * That is the worst failure mode a business system has, because it is
 * indistinguishable from "you have no data". A user cannot tell whether they are
 * looking at an empty pipeline or a broken one, and neither can support.
 *
 * The states are deliberately distinct:
 *
 *   loading        — work in progress
 *   offline        — the server cannot be reached; retry after reconnecting
 *   error          — something failed and MIGHT work on retry
 *   unauthorized   — the caller is not permitted; retrying will not help
 *   not-installed  — the module is not on this deployment
 *   empty          — genuinely nothing, which is the only one that is fine
 *
 * `unauthorized` and `not-installed` are separated from `error` because the
 * remedy is different: one needs an administrator, one needs an install, and
 * neither needs a retry button that will fail identically.
 */
import type { ReactNode } from 'react'
import { StateBoundary, EmptyState, type ExperienceState } from '@solum/ui'

/** The shape we need from a TanStack query, without depending on its generics. */
export interface QueryLike {
  isLoading: boolean
  isError?: boolean
  error?: unknown
  data?: unknown
  refetch?: () => unknown
}

export type QueryStateKind = 'loading' | 'offline' | 'error' | 'unauthorized' | 'not-installed' | 'ready'

/**
 * Classify a transport failure.
 *
 * The SDK throws `solum sdk: <status> <statusText> — <body>`, so the status is
 * recoverable from the message. Worth being explicit about a limit: an RLS
 * denial on a readable table returns 200 with zero rows, by design — the policy
 * FILTERS rather than refuses. That case is genuinely indistinguishable from
 * empty at this layer and is not claimed to be handled here.
 */
export function classifyError(error: unknown): Exclude<QueryStateKind, 'loading' | 'ready'> {
  const msg = error instanceof Error ? error.message : String(error ?? '')
  if (/\b(401|403)\b/.test(msg) || /permission denied|not authorized|JWT/i.test(msg)) return 'unauthorized'
  // PostgREST answers 404 with PGRST205/PGRST202 when a schema or function is
  // not exposed — which on this platform means the module is not installed (or
  // not in PGRST_DB_SCHEMAS), not that the user did something wrong.
  if (/PGRST205|PGRST202|does not exist|schema must be one of/i.test(msg)) return 'not-installed'
  if (/fetch|network|offline|Failed to fetch/i.test(msg)) return 'offline'
  return 'error'
}

/**
 * @param queries  REQUIRED — settling without data means something went wrong.
 * @param optional Contribute loading and error state, but are allowed to settle
 *                 with `null`. Not every query is mandatory: a form experience
 *                 is genuinely absent when none is configured, and treating that
 *                 as a failure blanks a page that was working. Found exactly
 *                 that way — `/e/afd_project/new` broke because its optional
 *                 experience query legitimately returns null.
 */
export function queryState(
  queries: QueryLike[], optional: QueryLike[] = [],
): { kind: QueryStateKind; error?: unknown } {
  const all = [...queries, ...optional]
  if (all.some((q) => q.isLoading)) return { kind: 'loading' }
  const failed = all.find((q) => q.isError || q.error)
  if (failed) return { kind: classifyError(failed.error), error: failed.error }
  // `data` absent without isError is the case that used to return null: the
  // query settled with nothing. Treat it as an error rather than as empty —
  // silently showing "nothing here" for a failed fetch is the bug.
  if (queries.some((q) => q.data == null)) return { kind: 'error' }
  return { kind: 'ready' }
}

export function QueryBoundary({ queries, optional = [], children, empty, label }: {
  queries: QueryLike[]
  /** Loading/error only — allowed to settle with null (see queryState). */
  optional?: QueryLike[]
  /** Rendered only once every query has data. */
  children: () => ReactNode
  /** Optional: shown instead of children when the content itself is empty. */
  empty?: ReactNode
  /** What the user was trying to see, e.g. "invoices" — used in the messages. */
  label?: string
}) {
  const { kind, error } = queryState(queries, optional)
  const what = label ? ` ${label}` : ''
  const retry = () => [...queries, ...optional].forEach((q) => q.refetch?.())
  let state: ExperienceState

  switch (kind) {
    case 'loading':
      state = { kind: 'loading', label: `Loading${what || ' this'}…` }
      break

    case 'unauthorized':
      // No retry: the same request will be refused the same way. Say who can
      // help instead of offering a button that cannot work.
      state = {
        kind: 'refused',
        title: 'You don’t have access to this',
        message: `Your account is not permitted to see${what || ' this'}. An administrator can grant access in People & Access.`,
      }
      break

    case 'not-installed':
      state = {
        kind: 'refused',
        title: 'That module isn’t installed here',
        message: `This deployment does not have the module that provides${what || ' this'}. An administrator can install it from Modules.`,
      }
      break

    case 'offline':
      state = {
        kind: 'offline',
        title: 'The server could not be reached',
        message: 'Check your connection and try again.',
        onRetry: retry,
      }
      break

    case 'error':
      state = { kind: 'error', title: 'Couldn’t load this', message: messageFor(error), onRetry: retry }
      break

    case 'ready':
      return <>{empty ?? children()}</>
  }
  return <StateBoundary state={state} />
}

/**
 * A message a user can act on, without leaking internals.
 *
 * The raw transport error contains URLs, schema names and SQL — useful in a log,
 * not on a screen. The status is kept because it is what support will ask for.
 */
function messageFor(error: unknown): string {
  const msg = error instanceof Error ? error.message : String(error ?? '')
  const status = /\b(\d{3})\b/.exec(msg)?.[1]
  if (/fetch|network|Failed to fetch/i.test(msg)) {
    return 'The server could not be reached. Check your connection and try again.'
  }
  return status
    ? `The server returned an error (${status}). Try again — if it keeps happening, quote this code to support.`
    : 'Something went wrong loading this. Try again.'
}

/** The genuinely-nothing state, kept here so every list words it the same way. */
export function NoRows({ what, action }: { what: string; action?: ReactNode }) {
  return <EmptyState action={action}>No {what} yet.</EmptyState>
}

/* ── the INLINE form of the same six states (UX-04 / UX-10, #389) ────────────
 *
 * <QueryBoundary> replaces a whole REGION with a spinner or an ErrorState. That
 * is right for a page and wrong for a sentence: the sites this exists for are
 * one line inside a panel that is otherwise fine — the SLA line on a case, the
 * thread footer, a queue tile. Swapping the panel for a spinner there loses the
 * record the user is reading.
 *
 * So the same classification, a tenth of the chrome. The wording is central on
 * purpose: 24 surfaces each inventing their own phrasing for "we don't know yet"
 * is how "no SLA on this case" got written as a fact in the first place. The
 * distinction that matters in every one of them is the LAST clause — an absence
 * we failed to read is not an absence.
 */
export function unresolvedText(kind: Exclude<QueryStateKind, 'ready'>, what: string): string {
  // Said the same way everywhere, because the failure mode is the same
  // everywhere: silence read as evidence.
  const notAbsence = ' — this is not the same as there being none.'
  switch (kind) {
    case 'loading': return `Loading ${what}…`
    case 'offline': return `Couldn’t reach the server for ${what}${notAbsence}`
    case 'unauthorized': return `You’re not permitted to see ${what}${notAbsence}`
    case 'not-installed': return `${what} isn’t available on this deployment.`
    case 'error': return `Couldn’t read ${what}${notAbsence}`
  }
}

/**
 * Renders nothing when the queries are ready — so the call site reads
 * `<Unresolved …/>` followed by the claim it is guarding, and the claim is only
 * reached once it is true.
 *
 * @param what a noun phrase that completes "Loading …" and "Couldn't read …",
 *             e.g. "the commitments on this case".
 */
export function Unresolved({ queries, what, className, testId }: {
  queries: QueryLike[]
  what: string
  className?: string
  testId?: string
}) {
  const { kind } = queryState(queries)
  if (kind === 'ready') return null
  /* IT CARRIES ITS OWN TREATMENT NOW (#2406). This rendered a bare `<div>`, so
     five call sites each threaded `crm.module.css`'s `.hint` in through
     `className` to stop it looking like body copy — a shared component asking
     every host to style it, which is the seam upside down. The default is the
     `.supporting` utility; `className` stays for a host that genuinely needs
     something else, and composes rather than replaces. */
  return (
    <div className={className ? `supporting ${className}` : 'supporting'}
      data-testid={testId ?? 'unresolved'} data-state={kind}>
      {unresolvedText(kind, what)}
    </div>
  )
}

/** True once every query has really answered — the guard for a factual claim. */
export function isResolved(queries: QueryLike[]): boolean {
  return queryState(queries).kind === 'ready'
}

/**
 * The EARLY-RETURN form, for the twenty-odd pages written as
 * `if (q.isLoading) return <Spinner/>` and then nothing (#389).
 *
 * That shape handles one of six states. The other five fall through to a page
 * rendered from `data ?? []` — an approvals inbox reading "Nothing awaiting your
 * decision", a burn report totalling €0, a scheduler board showing no stalled
 * jobs. Each is a confident answer to a question that was never answered.
 *
 * Returns null when everything is ready, so a page keeps its own layout:
 *
 *     const gate = queryGate([q], 'approvals')
 *     if (gate) return <div className={s.page}>{gate}</div>
 *
 * It is a plain function, not a component and not a hook — it is called
 * unconditionally, after every hook, and its result decides the early return.
 */
export function queryGate(queries: QueryLike[], label?: string, optional: QueryLike[] = []): ReactNode | null {
  if (queryState(queries, optional).kind === 'ready') return null
  return <QueryBoundary queries={queries} optional={optional} label={label}>{() => null}</QueryBoundary>
}
